import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import useCancelMaintenanceWorkflow from '../../Create/hooks/useCancelMaintenanceWorkflow'
import { ArrowUpRight, OctagonAlert } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import TextArea from '@/common/components/TextArea'
import { maintenanceWorkflowCancelSchema, MaintenanceWorkflowCancelSchemaValidationSchema } from '@/features/maintenanceWorkflow/schemas/cancelMaintenanceWorkflow.schema'

interface CancelMaintenanceWorkflowModalProps {
  maintenanceWorkflowId?: string
}

export const CancelMaintenanceWorkflowModal = ({ maintenanceWorkflowId }: CancelMaintenanceWorkflowModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)

  const { mutate: cancelMaintenanceWorkflow } = useCancelMaintenanceWorkflow({
    id: maintenanceWorkflowId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', maintenanceWorkflowId] })
      reset()
      setIsOpen(false)
    },
    onError: (error) => FormTransformers.errorsTransformer({ setError })(error),
  })

  const {
    register,
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<MaintenanceWorkflowCancelSchemaValidationSchema>({
    resolver: zodResolver(maintenanceWorkflowCancelSchema),
  });

  const onSubmit = (data: MaintenanceWorkflowCancelSchemaValidationSchema) => {
    cancelMaintenanceWorkflow(data)
  }

  useEffect(reset, [isOpen])

  return (
    <Modal
      Trigger={
        <div className='min-w-full'>
          <Button
            operationKlassToCheckPermission="Client::Maintenance::Workflows::Cancel"
            type="button"
            variant="error"
            className='flex justify-between w-full bg-danger-op rounded-none py-3'
          >
            <OctagonAlert className='max-w-4' />
            Cancelar Processo
            <ArrowUpRight className='max-w-4' />
          </Button>
        </div>
      }
      triggerAsChild
      title="Cancelar Processo de manutenção"
      subtitle="Você tem certeza?"
      subtitleClassName="mt-1 text-white-300"
      useCancellableFooter
      cancelButtonText='Voltar'
      confirmButtonText='Cancelar Processo'
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit((data) => onSubmit(data))}
      body={(
        <form className="flex flex-col gap-4">
          <TextArea
            className="text-white-800 opacity-80"
            label="Justificativa"
            {...register('cancel_reason')}
            errorMessage={errors.cancel_reason?.message || undefined}
          />
        </form>
      )}
    />
  )
}
